import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_WHEN_UNDER_MS = 3 * 24 * 60 * 60 * 1000; // refresh when < 3 days of the 7-day token remain
const CHECK_EVERY_MS = 30 * 60 * 1000;
const SESSION_EXPIRED_DETAILS = ['Token has expired', 'Invalid token', 'User not found'];

const AuthContext = createContext(null);

function tokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem('admin_token'));
  const [sessionExpired, setSessionExpired] = useState(false);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  const endSession = useCallback((message) => {
    localStorage.removeItem('admin_token');
    setImpersonating(false);
    setToken(null);
    setUser(null);
    if (message) {
      setSessionExpired(true);
      toast.error(message, { id: 'session-expired', duration: 8000 });
    }
  }, []);

  // Global 401 handler: an expired/invalid session token signs the user out cleanly
  // instead of leaving raw "Token has expired" errors inside individual screens.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        const detail = error?.response?.data?.detail;
        const ours = url.startsWith(`${API}/api/`) || url.startsWith('/api/');
        const isLogin = url.includes('/api/auth/login');
        if (status === 401 && ours && !isLogin && tokenRef.current && SESSION_EXPIRED_DETAILS.includes(detail)) {
          endSession('Your session has expired — please sign in again.');
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [endSession]);

  // Sliding session: while the user is active, swap a token nearing expiry for a fresh one.
  const refreshIfNeeded = useCallback(async () => {
    const current = tokenRef.current;
    if (!current || localStorage.getItem('admin_token')) return;
    const exp = tokenExpiry(current);
    if (!exp || exp - Date.now() > REFRESH_WHEN_UNDER_MS) return;
    try {
      const res = await axios.post(`${API}/api/auth/refresh`, null, { headers: { Authorization: `Bearer ${current}` } });
      if (res.data?.access_token) setToken(res.data.access_token);
    } catch {
      // 401s are handled by the interceptor; anything else is retried on the next check
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(refreshIfNeeded, CHECK_EVERY_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') refreshIfNeeded(); };
    document.addEventListener('visibilitychange', onFocus);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onFocus); };
  }, [refreshIfNeeded]);

  // Load user on mount if token exists
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/api/auth/me`);
          setUser(response.data);
          refreshIfNeeded();
        } catch (error) {
          console.error('Failed to load user:', error);
          // Token invalid/expired (interceptor already cleared it when the backend said so)
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/api/auth/login`, {
      email,
      password
    });
    setSessionExpired(false);
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    endSession();
  };

  // WMS single sign-on: we already hold a verified session token + user
  const loginWithToken = (newToken, newUser) => {
    setSessionExpired(false);
    setToken(newToken);
    setUser(newUser);
  };

  const impersonate = (newToken, newUser) => {
    localStorage.setItem('admin_token', token);
    setImpersonating(true);
    setUser(newUser);
    setToken(newToken);
  };

  const stopImpersonating = async () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.removeItem('admin_token');
      setImpersonating(false);
      setToken(adminToken);
      // Reload admin user from /me
      try {
        const res = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        setUser(res.data);
      } catch {
        setToken(null);
        setUser(null);
      }
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    loginWithToken,
    impersonate,
    stopImpersonating,
    impersonating,
    sessionExpired,
    clearSessionExpired: () => setSessionExpired(false),
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
